import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

const UrineRMForm = () => {
  const [reports, setReports] = useState([]);
  const [formData, setFormData] = useState({
    pt_name: "",
    age_sex: "",
    ref_by: "",
    report_date: "",
    quantity: "",
    color: "",
    appearance: "",
    ph: "",
    specific_gravity: "",
    glucose_sugar: "",
    albumin_protein: "",
    kitone: "",
    bile_salts: "",
    bile_pigment: "",
    urobilinogen: "",
    leukocyte_pus_cell: "",
    red_blood_cell: "",
    epithelial_cell: "",
    cast: "",
    bacteria: "",
    crystals: "",
    upt: "",
  });
  const [editId, setEditId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/urine-rmreports`);
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) {
      try {
        await axios.put(
          `${API_BASE_URL}/api/urine-rmreports/${editId}`,
          formData
        );
        setEditId(null);
        resetForm();
        fetchReports();
      } catch (error) {
        console.error("Error updating report:", error);
      }
    } else {
      try {
        await axios.post(`${API_BASE_URL}/api/urine-rmreports`, formData);
        resetForm();
        fetchReports();
      } catch (error) {
        console.error("Error adding report:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      pt_name: "",
      age_sex: "",
      ref_by: "",
      report_date: "",
      quantity: "",
      color: "",
      appearance: "",
      ph: "",
      specific_gravity: "",
      glucose_sugar: "",
      albumin_protein: "",
      kitone: "",
      bile_salts: "",
      bile_pigment: "",
      urobilinogen: "",
      leukocyte_pus_cell: "",
      red_blood_cell: "",
      epithelial_cell: "",
      cast: "",
      bacteria: "",
      crystals: "",
      upt: "",
    });
  };

  const handleEdit = async (id) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/urine-rmreports/${id}`
      );
      setFormData(response.data);
      setEditId(id);
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/urine-rmreports/${id}`);
        fetchReports();
      } catch (error) {
        console.error("Error deleting report:", error);
      }
    }
  };

  const handleView = async (id) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/urine-rmreports/${id}`
      );
      setSelectedReport(response.data);
    } catch (error) {
      console.error("Error viewing report:", error);
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">🧪 Urine RM Report</h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mb-5">
        <div className="row g-2">
          <div className="col-md-4">
            <label>Patient Name:</label>
            <input
              type="text"
              name="pt_name"
              className="form-control"
              value={formData.pt_name}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-2">
            <label>Age/Sex:</label>
            <input
              type="text"
              name="age_sex"
              className="form-control"
              value={formData.age_sex}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-3">
            <label>Referred By:</label>
            <input
              type="text"
              name="ref_by"
              className="form-control"
              value={formData.ref_by}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-3">
            <label>Report Date:</label>
            <input
              type="date"
              name="report_date"
              className="form-control"
              value={formData.report_date}
              onChange={handleChange}
            />
          </div>
        </div>

        <hr />

        <div className="row g-2">
          {[
            "quantity",
            "color",
            "appearance",
            "ph",
            "specific_gravity",
            "glucose_sugar",
            "albumin_protein",
            "kitone",
            "bile_salts",
            "bile_pigment",
            "urobilinogen",
            "leukocyte_pus_cell",
            "red_blood_cell",
            "epithelial_cell",
            "cast",
            "bacteria",
            "crystals",
            "upt",
          ].map((field, idx) => (
            <div key={idx} className="col-md-3">
              <label>{field.replace(/_/g, " ").toUpperCase()}:</label>
              <input
                type="text"
                name={field}
                className="form-control"
                value={formData[field]}
                onChange={handleChange}
              />
            </div>
          ))}
        </div>

        <div className="mt-4">
          <button type="submit" className="btn btn-success">
            {editId ? "Update Report" : "Add Report"}
          </button>
          {editId && (
            <button
              type="button"
              className="btn btn-secondary ms-2"
              onClick={() => {
                setEditId(null);
                resetForm();
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {/* Reports Table */}
      <h4>📋 All Urine RM Reports</h4>
      <table className="table table-striped">
        <thead>
          <tr>
            <th>ID</th>
            <th>Patient</th>
            <th>Age/Sex</th>
            <th>Doctor</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <tr key={report.id}>
              <td>{report.id}</td>
              <td>{report.pt_name}</td>
              <td>{report.age_sex}</td>
              <td>{report.ref_by}</td>
              <td>{report.report_date}</td>
              <td>
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={() => handleEdit(report.id)}
                >
                  ✏️
                </button>
                <button
                  className="btn btn-danger btn-sm me-2"
                  onClick={() => handleDelete(report.id)}
                >
                  🗑️
                </button>
                <button
                  className="btn btn-info btn-sm"
                  onClick={() => handleView(report.id)}
                >
                  👁️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Report Card */}
      {selectedReport && (
        <div className="card mt-4 print-area">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5>📝 Urine RM Report - {selectedReport.pt_name}</h5>
            <div>
              <button
                className="btn btn-secondary btn-sm me-2"
                onClick={() => window.print()}
              >
                🖨️ Print Report
              </button>
              <button
                className="btn btn-dark btn-sm"
                onClick={() => setSelectedReport(null)}
              >
                Close
              </button>
            </div>
          </div>
          <div className="card-body">
            <p>
              <strong>Patient:</strong> {selectedReport.pt_name}
            </p>
            <p>
              <strong>Age/Sex:</strong> {selectedReport.age_sex}
            </p>
            <p>
              <strong>Doctor:</strong> {selectedReport.ref_by}
            </p>
            <p>
              <strong>Date:</strong> {selectedReport.report_date}
            </p>
            <hr />
            <div className="row">
              {Object.entries(selectedReport).map(([key, value]) => {
                if (
                  [
                    "id",
                    "pt_name",
                    "age_sex",
                    "ref_by",
                    "report_date",
                    "created_at",
                    "updated_at",
                  ].includes(key)
                )
                  return null;
                return (
                  <div key={key} className="col-md-4 mb-2">
                    <strong>{key.replace(/_/g, " ").toUpperCase()}:</strong>{" "}
                    {value}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrineRMForm;
